import { useState, useRef, useCallback } from "react";
import { WAVEFORM_BAR_COUNT } from "@/modules/common/utils/audio.utils";

interface UseAudioRecorderOptions {
  onRecordingComplete?: (
    audioBlob: Blob,
    duration: number,
    waveformData: number[],
  ) => void;
  waveformBarCount?: number;
  maxDuration?: number;
  onMaxDurationReached?: () => void;
}

interface UseAudioRecorderReturn {
  isRecording: boolean;
  isPaused: boolean;
  isInitializing: boolean;
  recordingTime: number;
  audioLevels: number[];
  permissionError: string | null;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  cancelRecording: () => void;
  confirmRecording: () => void;
  clearPermissionError: () => void;
}

const AUDIO_LEVELS_BAR_COUNT = 35;

/**
 * Custom hook for audio recording with real-time visualization
 */
const useAudioRecorder = ({
  onRecordingComplete,
  waveformBarCount = WAVEFORM_BAR_COUNT,
  maxDuration,
  onMaxDurationReached,
}: UseAudioRecorderOptions = {}): UseAudioRecorderReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevels, setAudioLevels] = useState<number[]>(
    Array(AUDIO_LEVELS_BAR_COUNT).fill(4),
  );
  const [permissionError, setPermissionError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recordingTimeRef = useRef<number>(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const accumulatedLevelsRef = useRef<number[]>([]);
  const callbackRef = useRef({
    onRecordingComplete,
    waveformBarCount,
    maxDuration,
    onMaxDurationReached,
  });
  callbackRef.current = {
    onRecordingComplete,
    waveformBarCount,
    maxDuration,
    onMaxDurationReached,
  };
  const confirmRecordingRef = useRef<() => void>(() => {});
  const selectedMimeTypeRef = useRef<string>("");

  // Process accumulated audio levels into final waveform data
  const processAccumulatedLevels = useCallback((barCount: number): number[] => {
    const accumulated = accumulatedLevelsRef.current;
    if (accumulated.length === 0) {
      return Array(barCount).fill(16);
    }

    const waveformData: number[] = [];
    const samplesPerBar = Math.max(1, Math.floor(accumulated.length / barCount));

    for (let i = 0; i < barCount; i++) {
      const start = i * samplesPerBar;
      const end = Math.min(start + samplesPerBar, accumulated.length);
      let sum = 0;
      let count = 0;

      for (let j = start; j < end; j++) {
        sum += accumulated[j];
        count++;
      }

      const average = count > 0 ? sum / count : 0;
      // Normalize to match the expected height range (8-28)
      const normalizedHeight = Math.max(8, Math.min(28, (average / 255) * 20 + 8));
      waveformData.push(normalizedHeight);
    }

    return waveformData;
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopAudioLevelVisualization = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevels(Array(AUDIO_LEVELS_BAR_COUNT).fill(4));
  }, []);

  const startAudioLevelVisualization = useCallback((stream: MediaStream) => {
    try {
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 128;
      analyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const updateLevels = () => {
        if (!analyserRef.current) return;

        analyserRef.current.getByteFrequencyData(dataArray);

        // Calculate average level across all frequency bins for accumulation
        let totalLevel = 0;
        for (let i = 0; i < bufferLength; i++) {
          totalLevel += dataArray[i];
        }
        const avgLevel = totalLevel / bufferLength;
        accumulatedLevelsRef.current.push(avgLevel);

        const newLevels: number[] = [];
        const step = Math.floor(bufferLength / AUDIO_LEVELS_BAR_COUNT);

        for (let i = 0; i < AUDIO_LEVELS_BAR_COUNT; i++) {
          const index = i * step;
          const value = dataArray[index] || 0;
          const normalizedHeight = Math.max(4, (value / 255) * 20);
          newLevels.push(normalizedHeight);
        }

        setAudioLevels(newLevels);
        animationFrameRef.current = requestAnimationFrame(updateLevels);
      };

      updateLevels();
    } catch (error) {
      console.error("Error setting up audio visualization:", error);
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    timerRef.current = setInterval(() => {
      setRecordingTime((prev) => {
        const newTime = prev + 1;
        recordingTimeRef.current = newTime;

        const { maxDuration: max, onMaxDurationReached: onMax } =
          callbackRef.current;
        if (max && newTime >= max) {
          setTimeout(() => {
            confirmRecordingRef.current();
            onMax?.();
          }, 0);
        }

        return newTime;
      });
    }, 1000);
  }, [clearTimer]);

  const stopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    clearTimer();
    stopAudioLevelVisualization();
  }, [clearTimer, stopAudioLevelVisualization]);

  const startRecording = async () => {
    try {
      setIsInitializing(true);
      setPermissionError(null); // Clear any previous errors
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const getSupportedMimeType = () => {
        return "audio/mp4";
      };

      const mimeType = getSupportedMimeType();
      selectedMimeTypeRef.current = mimeType;

      const options = mimeType ? { mimeType } : undefined;
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      accumulatedLevelsRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Use the MediaRecorder's mimeType to ensure compatibility across devices
        // This is crucial for mobile devices (especially iOS) which don't support webm
        const mimeType =
          mediaRecorderRef.current?.mimeType ||
          selectedMimeTypeRef.current ||
          "audio/mp4";
        const audioBlob = new Blob(audioChunksRef.current, {
          type: mimeType,
        });
        const currentDuration = recordingTimeRef.current;
        const { onRecordingComplete: callback, waveformBarCount: barCount } =
          callbackRef.current;

        if (
          audioChunksRef.current.length > 0 &&
          currentDuration > 0 &&
          callback
        ) {
          const waveformData = processAccumulatedLevels(barCount);
          callback(audioBlob, currentDuration, waveformData);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      recordingTimeRef.current = 0;
      startTimer();
      startAudioLevelVisualization(stream);
      setIsInitializing(false);
    } catch (error: any) {
      console.error("Error accessing microphone:", error);
      setIsInitializing(false);
      
      // Provide user-friendly error messages based on error type
      if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
        setPermissionError(
          "Microphone access denied. Please enable microphone permissions in your browser settings to record voice notes."
        );
      } else if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
        setPermissionError(
          "No microphone found. Please connect a microphone to record voice notes."
        );
      } else if (error.name === "NotReadableError" || error.name === "TrackStartError") {
        setPermissionError(
          "Microphone is already in use by another application. Please close other apps using the microphone."
        );
      } else {
        setPermissionError(
          "Unable to access microphone. Please check your browser settings and try again."
        );
      }
    }
  };

  const clearPermissionError = useCallback(() => {
    setPermissionError(null);
  }, []);

  const pauseRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      clearTimer();
    }
  }, [clearTimer]);

  const resumeRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "paused"
    ) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      startTimer();
    }
  }, [startTimer]);

  const cancelRecording = useCallback(() => {
    audioChunksRef.current = [];
    accumulatedLevelsRef.current = [];
    recordingTimeRef.current = 0;
    stopRecording();
    setIsRecording(false);
    setIsPaused(false);
    setRecordingTime(0);
  }, [stopRecording]);

  const confirmRecording = useCallback(() => {
    stopRecording();
    setIsRecording(false);
    setIsPaused(false);
  }, [stopRecording]);

  // Keep ref updated for use in timer callback
  confirmRecordingRef.current = confirmRecording;

  return {
    isRecording,
    isPaused,
    isInitializing,
    recordingTime,
    audioLevels,
    permissionError,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    confirmRecording,
    clearPermissionError,
  };
};

export default useAudioRecorder;
